import React, { useEffect, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import TranscriptBrowser from './TranscriptBrowser';
import BackfillPanel from './BackfillPanel';
import BackfillRequestPanel from './BackfillRequestPanel';
import TrackedChannelsPanel from './TrackedChannelsPanel';
import CoveragePanel from './CoveragePanel';
import ExtractionStudio from './ExtractionStudio';
import AutoFetchPanel from './AutoFetchPanel';
import ChatterDashboard from './ChatterDashboard';
import NarrativeTracker from './NarrativeTracker';
import RelationshipMap from './RelationshipMap';
import PropagationPanel from './PropagationPanel';

const GOLD = 'rgb(var(--brand-gold-rgb))';

// The YouTube tab is split into two bands:
//   • ANALYSIS (public, every Research OS user) — the OUTPUT: Overview summary, the deterministic
//     Narrative tracker (scoring), and Themes (emergent theme buckets, Phase 2).
//   • PIPELINE (admin only) — the ingest PLUMBING: coverage, backfill, fetch, transcripts, extraction.
const ANALYSIS = [
  { id: 'overview', label: 'Overview' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'themes', label: 'Themes' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'signal', label: 'Signal' },
];
const PIPELINE = [
  { id: 'coverage', label: 'Coverage', ready: true },
  { id: 'backfill', label: 'Backfill & Runs', ready: true },
  { id: 'autofetch', label: 'Auto-Fetch', ready: true },
  { id: 'transcripts', label: 'Transcript Viewer', ready: true },
  { id: 'extract', label: 'Extraction (DeepSeek)', ready: true },
  { id: 'sources', label: 'Sources', ready: false },
];

function TabBtn({ on, ready = true, children, onClick }) {
  return (
    <button
      disabled={!ready}
      onClick={ready ? onClick : undefined}
      className={`rounded px-3 py-1.5 text-sm transition-colors ${ready ? '' : 'cursor-not-allowed opacity-40'}`}
      style={on
        ? { color: GOLD, border: `1px solid ${GOLD}`, fontWeight: 500 }
        : { border: '1px solid transparent', color: '#6b7280' }}
    >
      {children}{!ready && ' · soon'}
    </button>
  );
}

// Themes — the emergent theme taxonomy: top focus buckets (mention counts + member themes) grown
// from what the channels actually discuss, plus the visibly-held unclassified pool.
function StatTile({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800">
      <div className="font-serif text-2xl tabular-nums text-gray-900 dark:text-gray-100">{value}</div>
      <div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">{label}</div>
    </div>
  );
}

function BucketRow({ b, max, open, onToggle }) {
  const pct = max ? Math.max(3, Math.round((b.count / max) * 100)) : 0;
  return (
    <div className="border-b border-gray-100 last:border-0 dark:border-gray-800/60">
      <button onClick={onToggle} className="group flex w-full items-center gap-3 py-2 text-left">
        <span className="w-4 shrink-0 text-[10px] text-gray-400">{open ? '▾' : '▸'}</span>
        <span className="w-44 shrink-0 truncate text-sm text-gray-800 dark:text-gray-100" title={b.label}>{b.label}</span>
        <span className="relative h-4 flex-1 overflow-hidden rounded bg-gray-100 dark:bg-gray-800/60">
          <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${pct}%`, background: GOLD, opacity: 0.85 }} />
        </span>
        <span className="w-10 shrink-0 text-right text-sm tabular-nums font-medium text-gray-700 dark:text-gray-200">{b.count}</span>
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 pb-3 pl-11 pr-2">
          {b.themes.map((t) => (
            <span key={t} className="rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400" title={t}>
              {t.length > 52 ? `${t.slice(0, 52)}…` : t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemesPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);        // expanded bucket label
  const [poolOpen, setPoolOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    researchosService.getThemes()
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr('Could not load themes.'); });
    return () => { alive = false; };
  }, []);

  if (err) return <div className="text-sm text-red-500">{err}</div>;
  if (!data) return <div className="text-sm text-gray-400">Loading themes…</div>;

  const s = data.stats || {};
  const max = data.buckets?.[0]?.count || 0;   // buckets arrive sorted desc
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Themes</h2>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          Where the conversation is concentrated — emergent focus categories grown from {s.transcripts} transcripts,
          not a fixed taxonomy.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Focus buckets" value={s.buckets ?? '—'} />
        <StatTile label="Transcripts" value={s.transcripts ?? '—'} />
        <StatTile label="Themes bucketed" value={s.bucketed ?? '—'} />
        <StatTile label="In pool" value={s.pool ?? '—'} />
      </div>

      <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em]" style={{ color: GOLD }}>
          Focus buckets · by mention count
        </div>
        {(data.buckets || []).map((b) => (
          <BucketRow key={b.label} b={b} max={max}
            open={open === b.label} onToggle={() => setOpen(open === b.label ? null : b.label)} />
        ))}
      </div>

      {(data.pool || []).length > 0 && (
        <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-800">
          <button onClick={() => setPoolOpen((v) => !v)} className="flex w-full items-center justify-between text-left">
            <span className="text-[10px] font-medium uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">
              Unclassified pool · {data.pool.length} held (not a market theme yet)
            </span>
            <span className="text-[10px] text-gray-400">{poolOpen ? '▾ hide' : '▸ show'}</span>
          </button>
          {poolOpen && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {data.pool.map((t) => (
                <span key={t} className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:text-gray-400" title={t}>
                  {t.length > 52 ? `${t.slice(0, 52)}…` : t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ResearchPipelinePage() {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    let alive = true;
    researchosService.getScope()
      .then((r) => {
        if (!alive) return;
        setScope(r.data);
        // Quorum team members (linked, non-admin) land on the request workflow — their whole
        // surface here is Backfill & Runs + Signal (the 2026-09-25 opening of Chatter).
        if (!r.data?.is_admin && r.data?.can_view) setTab('backfill');
      })
      .catch(() => { if (alive) setScope({ is_admin: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="text-gray-500 dark:text-gray-400">Loading…</div>;

  const isAdmin = !!scope?.is_admin;
  // Quorum = admin-linked ResearchOS team member (scope.can_view mirrors the backend
  // IsResearchOSQuorum rule). Non-admin quorum users get exactly TWO tabs.
  const isQuorumOnly = !isAdmin && !!scope?.can_view;
  const isPipelineTab = PIPELINE.some((s) => s.id === tab);

  return (
    <div>
      {/* unified tab bar — Analysis first (public), then the admin Pipeline band.
          Quorum-only users see just [Backfill & Runs, Signal]. */}
      <nav className="mb-5 flex flex-wrap items-center gap-1">
        {isQuorumOnly ? (
          <>
            <TabBtn on={tab === 'backfill'} onClick={() => setTab('backfill')}>Backfill &amp; Runs</TabBtn>
            <TabBtn on={tab === 'coverage'} onClick={() => setTab('coverage')}>Coverage</TabBtn>
            <TabBtn on={tab === 'signal'} onClick={() => setTab('signal')}>Signal</TabBtn>
          </>
        ) : (
          <>
            {ANALYSIS.map((t) => (
              <TabBtn key={t.id} on={tab === t.id} onClick={() => setTab(t.id)}>{t.label}</TabBtn>
            ))}
            {isAdmin && (
              <>
                <span className="mx-2 h-4 w-px bg-gray-200 dark:bg-gray-700" />
                <span className="mr-1 text-[9px] font-medium uppercase tracking-[0.15em] text-gray-400">Pipeline</span>
                {PIPELINE.map((t) => (
                  <TabBtn key={t.id} on={tab === t.id} ready={t.ready} onClick={() => setTab(t.id)}>{t.label}</TabBtn>
                ))}
              </>
            )}
          </>
        )}
      </nav>

      {/* pipeline intro (admin, pipeline tabs only) */}
      {isAdmin && isPipelineTab && (
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Raw research ingested from external sources → cheap-model claim extraction → judgment.
          Stored sector-neutral; relevance is derived downstream.
        </p>
      )}

      {/* ── analysis (public) ── */}
      {tab === 'overview' && <ChatterDashboard />}
      {tab === 'narrative' && <NarrativeTracker />}
      {tab === 'themes' && <ThemesPanel />}
      {tab === 'relationships' && <RelationshipMap isAdmin={isAdmin} />}
      {tab === 'signal' && <PropagationPanel />}

      {/* ── quorum team: request→approve backfill + read-only tracked channels ── */}
      {isQuorumOnly && tab === 'backfill' && <BackfillRequestPanel />}
      {isQuorumOnly && tab === 'coverage' && <TrackedChannelsPanel />}

      {/* ── pipeline (admin) ── */}
      {isAdmin && tab === 'coverage' && <CoveragePanel />}
      {isAdmin && tab === 'backfill' && <BackfillPanel />}
      {isAdmin && tab === 'autofetch' && <AutoFetchPanel />}
      {isAdmin && tab === 'transcripts' && <TranscriptBrowser sourceType="youtube" />}
      {isAdmin && tab === 'extract' && <ExtractionStudio sourceType="youtube" />}
    </div>
  );
}
